import { useEffect, useRef, useState } from 'react';
import { FiImage } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { ExternalLinkIcon, QuestionIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormErrorMessage,
  HStack,
  Image,
  Input,
  InputGroup,
  Link,
  Select,
  Spinner,
  Text,
  Textarea,
  useToast,
  VStack,
  Switch,
  Tooltip,
  Tag,
  TagLabel,
  TagLeftIcon,
} from '@chakra-ui/react';
// @ts-ignore
import Mobile from '../../../assets/icons/mobile.png';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import { updateAppDetails } from '../../../redux/appBuilder/appDetails';
import { getAppDetails } from '../../../redux/appBuilder/appDetails/selectors';
import { Application } from '../../../redux/appBuilder/appDetails/types';
import {
  updateAppActiveStep,
  updateAppVerified,
  updateSelectedProviders,
  updateUpdatedImage,
} from '../../../redux/appBuilder/stepperDetails';
import {
  getAppActiveStep,
  getAppStepperDetails,
  getAppType,
} from '../../../redux/appBuilder/stepperDetails/selectors';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  getImageUrl,
  registerZkFetchApplication,
} from '../../../utils/app-builder/register-application';
import { generateEncryptionKeys } from '../../../utils/generate-wallet-address';
import { useDebounce } from '../../../utils/logs/send-logs';

const availablePlatforms = [
  { value: 'FLUTTER', label: 'Flutter', disabled: false },
  { value: 'REACT_NATIVE', label: 'React Native', disabled: false },
  { value: 'KOTLIN', label: 'Kotlin (Coming Soon)', disabled: true },
  { value: 'SWIFT', label: 'Swift (Coming Soon)', disabled: true },
];

export const NewAppDetails = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const navigate = useNavigate();
  const applicationImageUploadRef = useRef<HTMLInputElement | null>(null);

  const activeStep = useAppSelector(getAppActiveStep);
  const { width, height } = useWindowDimensions();
  const appData = useAppSelector(getAppDetails);
  const appStepperDetails = useAppSelector(getAppStepperDetails);
  const appType = useAppSelector(getAppType);
  // const appType = useParams().appType || ''
  const userEmailId = useAppSelector((state) => state.user.emailId);
  const emailIdSplit = userEmailId?.split('@');
  const emailDomain = emailIdSplit?.[1];

  const [appPlatformSelection, setAppPlatformSelection] = useState(
    appData?.appPlatform || ''
  );
  const [appPlatformError, setAppPlatformError] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [appName, setAppName] = useState(appData?.name || '');
  const debouncedSearchTerm = useDebounce(appName, 500);
  const [appNameInputTouched, setAppNameInputTouched] = useState(false);
  const [appNameError, setAppNameError] = useState('');
  const [projectName] = useState(appData?.projectName || emailDomain || '');
  const [appDescription, setAppDescription] = useState(appData?.description || '');
  const [appImage, setAppImage] = useState(appData?.appImageUrl || '');
  const [appImageError, setAppImageError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [isRecurring, setIsRecurring] = useState(appData?.isRecurring || false);

  const handleChange = (e: any) => {
    setAppPlatformSelection(e.target.value);
    setAppPlatformError('');
  };

  const toTitleCase = (word: string): string => {
    if (word.length === 0) {
      return word;
    }

    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  };

  const handleIframeLoad = () => {
    setIsVideoLoading(false);
  };

  const handleImageChange = (e: any) => {
    const file = e.target.files[0];
    if (file) {
      if (appStepperDetails.editAction) {
        dispatch(updateUpdatedImage(true));
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setAppImage(reader.result as string);
        setImagePreview(reader.result as string);
      };

      reader.readAsDataURL(file);
      setAppImageError('');
    }
  };

  const submitZkFetchApp = async () => {
    // check if appName is empty or contains only spaces
    if (!appName.trim()) {
      setAppNameError('App Name is required');
      return;
    }

    const { address, privateKey } = await generateEncryptionKeys();

    dispatch(
      updateAppDetails({
        name: appName,
        appId: address,
        privateKey: privateKey,
        projectName: projectName,
        description: appDescription,
        appType: appType as string,
        isRecurring: isRecurring,
      })
    );
    dispatch(updateAppVerified(true));

    const newAppData: Application = {
      name: appName,
      appId: address,
      description: appDescription,
      appImageExists: false,
      projectName: projectName,
      appType: appType as string,
      isRecurring: isRecurring,
    };
    // dispatch(updateAppActiveStep(activeStep + 1))
    // return
    const data = await registerZkFetchApplication(newAppData);
    if (data?.isSuccess) {
      dispatch(updateAppActiveStep(activeStep + 1));
      // navigate(`/my-applications/code-preview/${address}`)
    } else {
      toast({
        title: 'Failed to register application',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const validateAppName = (name: string) => {
    // Validate app name with following rules
    // 1. App name should not be empty
    // 2. App name should not contain only spaces
    // 3. App name should not contain special characters except space
    // 4. App Name should not start with a number or space
    // 5. App Name should not contain more than 50 characters
    // 6. App Name should not contain less than 3 characters

    if (!name.trim()) {
      setAppNameError('App Name is required');
      return false;
    }

    if (name.trim().length < 3) {
      setAppNameError('App Name should contain atleast 3 characters');
      return false;
    }

    if (name.trim().length > 50) {
      setAppNameError('App Name should not contain more than 50 characters');
      return false;
    }

    if (!/^[a-zA-Z0-9 ]*$/.test(name)) {
      setAppNameError('App Name should not contain special characters');
      return false;
    }

    if (/^[\d ]/.test(name)) {
      setAppNameError('App Name should not start with a number or space');
      return false;
    }

    setAppNameError('');
    return true;
  };

  useEffect(() => {
    if (appNameInputTouched) {
      validateAppName(appName);
    }
  }, [debouncedSearchTerm]);

  const handleBlur = (e: any) => {
    setAppNameInputTouched(true);
  };

  const handleNext = async () => {
    if (appType === 'ZK_FETCH') {
      if (!appStepperDetails.editAction) {
        await submitZkFetchApp();
      } else {
        dispatch(updateAppActiveStep(activeStep + 1));
      }
      return;
    }

    // check if appName is empty or contains only spaces
    if (!validateAppName(appName)) {
      return;
    }

    if (!appPlatformSelection && appType === 'MOBILE') {
      setAppPlatformError('App Platform is required');
      return;
    }

    if (!appImage) {
      setAppImageError('App Logo is required');
      return;
    }

    setIsLoading(true);

    dispatch(
      updateAppDetails({
        name: appName,
        projectName: projectName,
        description: appDescription,
        appPlatform: appPlatformSelection,
        appImageUrl: appImage,
        appImageExists: !!appImage,
        appType: appType as string,
        isRecurring: isRecurring,
      })
    );

    if (appStepperDetails.editAction) {
      let newAppImageUrl = '';
      if (imagePreview && appImage !== appData.appImageUrl) {
        const data = await getImageUrl(appImage, appData.appId);
        if (data?.error) {
          toast({
            title: 'Failed to upload image',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
          return;
        }

        newAppImageUrl = data?.url as string;
      }

      dispatch(updateSelectedProviders(appData?.httpProvider || []));
    }

    setIsLoading(false);
    dispatch(updateAppActiveStep(activeStep + 1));
  };

  return (
    <Card
      w={width > 1740 ? '1300px' : '95%'}
      variant="outline"
      mt={6}
      backgroundColor="#FFF"
      borderRadius="12px"
      h="fit-content"
    >
      <CardBody p={0}>
        <VStack h="72vh" overflowY="scroll">
          <HStack h="100%" w="100%">
            <VStack
              w="50%"
              h="100%"
              p="36px"
              spacing={width > 1500 ? '30px' : '16px'}
              align="flex-start"
            >
              <HStack spacing="12px" alignItems="center">
                <Text className="medium-font" fontWeight="bold" fontFamily="inter">
                  {appStepperDetails.editAction ? 'Edit' : 'Create a new'}{' '}
                  {appType === 'ZK_FETCH' ? 'ZK Fetch' : toTitleCase(appType as string)}{' '}
                  App
                </Text>
                <Text
                  className="small-font"
                  textDecoration="underline"
                  color="gray"
                  mt="3px"
                  fontFamily="inter"
                >
                  <Link href="https://demo.reclaimprotocol.org/" isExternal>
                    Try out a demo
                    <ExternalLinkIcon color="gray" mt="-2px" ml="2px" bgSize={4} />
                  </Link>
                </Text>
              </HStack>

              <FormControl isInvalid={!!appNameError.length}>
                <Text ml="5px" mb="1px" color="gray.600" className="small-font">
                  Application Name *
                </Text>
                <InputGroup>
                  <Input
                    value={appName}
                    onFocus={handleBlur}
                    onChange={(e) => {
                      setAppName(e.target.value);
                      setAppNameError('');
                    }}
                    placeholder="Application Name"
                    fontSize={width > 1500 ? '16px' : '12px'}
                    required={true}
                    px={5}
                    py={6}
                    borderRadius={16}
                  />
                </InputGroup>
                <FormErrorMessage ml="5px" className="small-font">
                  {appNameError}
                </FormErrorMessage>
              </FormControl>
              <FormControl>
                <Text color="gray.600" ml="5px" mb="1px" className="small-font">
                  Application Description
                </Text>
                <Textarea
                  w="100%"
                  px={5}
                  py={6}
                  borderRadius={16}
                  placeholder=" Application Description"
                  fontSize={width > 1500 ? '16px' : '12px'}
                  value={appDescription}
                  onChange={(e) => setAppDescription(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <HStack>
                  <Text color="gray.600" ml="5px" mb="1px" className="small-font">
                    Recurring Proofs
                  </Text>
                  <Tooltip
                    hasArrow
                    bg="gray.600"
                    placement="right"
                    label="Recurring proofs allow your application to request updated proofs periodically. This ensures that a user's verification status remains fresh by sending users notifications to reverify."
                  >
                    <QuestionIcon />
                  </Tooltip>
                </HStack>
                <HStack mt={2} ml={2}>
                  <Switch
                    isChecked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                  />
                  <Text fontSize="sm" color="gray.600">
                    Activate Recurring Proofs
                  </Text>
                </HStack>
              </FormControl>
              {appType === 'MOBILE' && (
                <>
                  <FormControl isInvalid={!!appPlatformError.length} id="appPlatform">
                    <Text ml="5px" mb="1px" color="gray.600" className="small-font">
                      Application Platform *
                    </Text>
                    <Select
                      placeholder="Select App Platform"
                      value={appPlatformSelection}
                      fontSize={width > 1500 ? '16px' : '12px'}
                      onChange={handleChange}
                      variant="filled"
                      isInvalid={!!appPlatformError.length}
                      style={{
                        color: 'black',
                        borderRadius: '15px',
                        border: '1px solid #D0D5DD',
                        backgroundColor: 'transparent',
                        padding: '6px 5px',
                        paddingLeft: '20px',
                        height: '50px',
                      }}
                    >
                      {availablePlatforms?.map((platform) => (
                        <option
                          key={platform?.value}
                          value={platform.value}
                          disabled={platform.disabled}
                        >
                          {platform.label}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage ml="5px" className="small-font">
                      {appPlatformError}
                    </FormErrorMessage>
                  </FormControl>
                </>
              )}
              {appType !== 'ZK_FETCH' && (
                <FormControl isInvalid={!!appImageError.length}>
                  <Text ml="5px" mb="1px" color="gray.600" className="small-font">
                    App Logo *
                  </Text>
                  <Input
                    type="file"
                    accept="image/*"
                    hidden
                    ref={applicationImageUploadRef}
                    onChange={handleImageChange}
                  />
                  <Button
                    _hover={{ bgColor: 'gray.50' }}
                    borderRadius="16px"
                    bgColor="white"
                    borderWidth="2px"
                    borderStyle="dashed"
                    borderColor="gray.200"
                    h={width > 1500 ? '150px' : '100px'}
                    w={width > 1500 ? '150px' : '100px'}
                    onClick={() => applicationImageUploadRef?.current?.click()}
                  >
                    <Flex direction="column">
                      {imagePreview || appData.appImageExists ? (
                        <Box
                          borderWidth={1}
                          borderRadius="lg"
                          overflow="hidden"
                          boxShadow="md"
                        >
                          <Image
                            src={
                              imagePreview
                                ? imagePreview
                                : appData.appImageExists
                                  ? appData.appImageUrl
                                  : ''
                            }
                            h={width > 1500 ? '100px' : '75px'}
                            w={width > 1500 ? '100px' : '75px'}
                            alt="Uploaded preview"
                          />
                        </Box>
                      ) : (
                        <Flex
                          direction="column"
                          alignItems="center"
                          justifyContent="center"
                          gap={1}
                        >
                          <FiImage fontSize="2rem" color="gray" />
                          <Text
                            ml="5px"
                            color="gray.500"
                            fontSize={width > 1500 ? '14px' : '12px'}
                            fontWeight={400}
                          >
                            App Logo
                          </Text>
                        </Flex>
                      )}
                    </Flex>
                  </Button>
                  <FormErrorMessage ml="5px" className="small-font">
                    {appImageError}
                  </FormErrorMessage>
                </FormControl>
              )}
            </VStack>
            <VStack
              w="50%"
              h="100%"
              borderRadius="12px"
              align="flex-end"
              justify="center"
              bgColor="black"
              spacing={0}
            >
              <Box width="95%" height="0" paddingBottom="95%" position="relative">
                {isVideoLoading && (
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    width="100%"
                    height="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="12px"
                    bgColor="black"
                  >
                    <Spinner size="xl" color="white" />
                  </Box>
                )}
                {
                  <Box
                    as="iframe"
                    position="absolute"
                    top="0"
                    left="0"
                    width="95%"
                    height="100%"
                    src={
                      appType === 'MOBILE'
                        ? 'https://www.loom.com/embed/d7b133b8931c49749837e2c21390eef2?sid=cf3c9672-371e-44b3-b4b9-2dfd32654c24'
                        : appType === 'ZK_FETCH'
                          ? 'https://www.loom.com/embed/cbbe93ad55ad43209ae9c7d69808a47c?sid=78d67a55-c74a-4603-ab04-99604f45d22d'
                          : 'https://www.loom.com/embed/4e84a5df3d534a24a0fb5d2bc17ee521?sid=b88b15bd-ac72-46a3-a0fa-aa8d216a5764'
                    }
                    frameBorder="0"
                    allowFullScreen
                    borderRadius="12px"
                    onLoad={handleIframeLoad}
                    opacity={isVideoLoading ? 0 : 1}
                    bgColor="black"
                  />
                }
              </Box>
            </VStack>
          </HStack>
        </VStack>
        <VStack className="stepper-bar" alignItems="flex-end">
          <Flex alignItems="center" gap="16px" justifyContent="flex-end">
            <Text color="gray.500" className="small-font">
              {appType === 'ZK_FETCH' ? '1/3' : '1/4'}
            </Text>
            <Button
              className="small-font stepper-next-button"
              isLoading={isLoading}
              onClick={handleNext}
              fontFamily="inter"
              _hover={{
                transform: 'scale(1.05)',
              }}
            >
              Next
            </Button>
          </Flex>
        </VStack>
      </CardBody>
    </Card>
  );
};
